import React from 'react';
import { Link } from 'react-router-dom';
import { usePageLastUpdated } from '../../hooks/usePageLastUpdated';
import './ProjectPage.css';

const PAGE_LAST_UPDATED = { dateTime: '2011-03-06', displayDate: 'March 6, 2011' };

const NavLinks = () => (
  <p className="text-small text-italic text-gray">
    <a href="#top">Go to top</a> | <a href="#contents">Go to contents</a>
  </p>
);

const SimWorkerPage = () => {
  usePageLastUpdated(PAGE_LAST_UPDATED);

  return (
    <div className="project-page" id="top">
      <h1 className="title">A Parallel Computation Library for Discrete Event Simulations</h1>
      <p>
        <Link to="/">Alex Bikfalvi</Link>,{' '}
        <a href="http://www.it.uc3m.es/~jgr" target="_blank" rel="noopener noreferrer">
          Jaime García-Reinoso
        </a>
      </p>

      <h1>Abstract</h1>
      <p>
        With the advent of multi-core processors, large scale simulations running on desktop-class systems can benefit
        from true parallel multitasking as opposed to most common time-sharing multitasking. However, most common
        simulation software is unsuitable to parallel execution due to the dependency chain between different tasks.
        When such execution is possible, it is usually available only for small sequences of code and spawning a new
        process or thread to run it parallel will add a significant overhead. This white paper presents a cross-platform
        library for parallel execution of simulation tasks based on the well-known concept of worker threads.
      </p>

      <h1 id="contents">On This Page</h1>
      <ol type="I">
        <li><a href="#c1">Concept Overview</a></li>
        <li><a href="#c2">Worker Threads</a></li>
        <li><a href="#c3">Work Items</a></li>
        <li><a href="#c4">Work Dispatching and Synchronization</a></li>
        <li><a href="#c5">Example</a></li>
        <li><a href="#c6">Performance Benefit</a></li>
      </ol>

      <h1 id="c1">I. Concept Overview</h1>
      <p>
        Our library borrows the concept of <em>worker threads</em>, implemented by the kernel of many operating
        systems, to create a pool of threads that can - when needed - execute work on the simulator's behalf. These
        worker threads are created before the simulation starts and are available throughout the simulation interval.
        When certain pieces of simulation code can be executed in parallel, the simulator creates a <em>work item</em>{' '}
        that &quot;describes&quot; the work and adds it to the queue of any worker thread. If the thread is busy
        executing other code, the new work item gets postponed until the thread becomes available.
      </p>
      <p>Design requirements:</p>
      <ul>
        <li>
          When there is no work to perform all worker threads are in a <em>wait state</em> (i.e. they don't use any CPU
          cycles).
        </li>
        <li>
          A worker thread <em>resumes</em> execution only when a new work item is added to its queue.
        </li>
        <li>
          The main simulation thread that created the work item can <em>wait</em> for the completion of the work item
          (again, the wait does not consume any CPU cycles).
        </li>
      </ul>
      <NavLinks />

      <h1 id="c2">II. Worker Threads</h1>
      <p>
        The simulator can start a number of worker threads at the beginning of the simulation and stop them at the end.
        The following two classes implement a worker thread and a set of worker threads respectively.
      </p>
      <table className="information" style={{ width: '700px' }}>
        <tbody>
          <tr>
            <th className="strong" style={{ width: '150px' }}>Class</th>
            <th style={{ width: '550px' }}>Description</th>
          </tr>
          <tr>
            <td className="strong">
              <a href="/projects/simworker/code/html/class_c_sim_worker.html">CSimWorker</a>
            </td>
            <td>Cross-platform implementation of one worker thread (Windows API and POSIX threads)</td>
          </tr>
          <tr>
            <td className="strong">
              <a href="/projects/simworker/code/html/class_c_sim_workers.html">CSimWorkers</a>
            </td>
            <td>A set of worker threads.</td>
          </tr>
        </tbody>
      </table>
      <p>
        For each worker thread, you can specify a worker <em>queue size</em> where new work items can be stored while
        the thread is busy. For performance reasons, the worker queue has a fixed size for the lifetime of the worker
        thread. In order to set an adequate value for the queue size that can accommodate your application, keep in mind
        that every entry in the worker thread queue is <em>only</em> a pointer to a work item. For example, on a
        32-bit system a queue size with 1000 entries will use only 4 KB of memory.
      </p>
      <NavLinks />

      <h1 id="c3">III. Work Items</h1>
      <p>
        A work item is an object that encapsulates the work to be performed by the worker thread. Because we are using
        C++, a work item contains the following information:
      </p>
      <ul>
        <li>The <em>object</em> and the <em>function pointer</em> the worker thread will call to execute the work item.</li>
        <li>Two <em>formal parameters</em>, one for input and one for output.</li>
      </ul>
      <div className="box">
        <p><strong>Why do we use both an object and a function pointer inside a work item?</strong></p>
        <p>
          As opposed to C, where we normally would only need a function pointer, using C++ and calling a function in the
          context of an object, allows us to share with that function (which is executing on the worker thread) any data
          that would normally be available to that object. This minimizes the number of formal parameters that have to be
          sent through the stack, and much of the data can be shared natively. Keep in mind you have to synchronize
          shared data, though!
        </p>
      </div>
      <div className="box">
        <p><strong>What do I do if I need more (or less) parameters for my work item function?</strong></p>
        <p>1. Use a structure or a class for each parameter, or;</p>
        <p>
          2. Derive the <a href="/projects/simworker/code/html/class_c_sim_work_item.html">CSimWorkItem</a> base class
          and make your own template with more (or less) parameters (use the current{' '}
          <a href="/projects/simworker/code/html/class_c_sim_work_item_impl.html">CSimWorkItemImpl</a> class as a
          model).
        </p>
      </div>
      <table className="information" style={{ width: '700px' }}>
        <tbody>
          <tr>
            <th className="strong" style={{ width: '150px' }}>Class</th>
            <th style={{ width: '550px' }}>Description</th>
          </tr>
          <tr>
            <td className="strong">
              <a href="/projects/simworker/code/html/class_c_sim_work_item.html">CSimWorkItem</a>
            </td>
            <td>Base abstract class for a generic work item object.</td>
          </tr>
          <tr>
            <td className="strong">
              <a href="/projects/simworker/code/html/class_c_sim_work_item_impl.html">CSimWorkItemImpl</a>
            </td>
            <td>Derived class for a specific work item object for a function with two formal parameters.</td>
          </tr>
        </tbody>
      </table>
      <NavLinks />

      <h1 id="c4">IV. Work Dispatching and Synchronization</h1>
      <p>After creating and starting a set of worker threads, the simulator can use them as follows:</p>
      <ul>
        <li>Create one or more work items.</li>
        <li>
          Enqueue the work items to the worker thread or threads. If the simulator uses a set of worker threads (
          <a href="/projects/simworker/code/html/class_c_sim_workers.html">CSimWorkers</a> class), the work items are
          allocated to the existing worker threads in a round-robin fashion.
        </li>
        <li>Wait for the work items to complete.</li>
      </ul>
      <div className="box">
        <p><strong>How does the simulator wait for the work items to complete?</strong></p>
        <p>
          Every work item has a <em>wait</em> function. The function is blocking, meaning the simulator thread is
          suspended until the corresponding work item finishes execution on the worker thread.
        </p>
        <p>
          With the current implementation, on UNIX-based systems the simulator must wait for work items to complete in
          the same order as they were scheduled to worker threads, otherwise the program will deadlock. On Windows there
          is no restriction. Why? The worker thread may <strong>only</strong> signal the completion of the work item
          when the simulator thread waits for its completion (the signal condition on the worker thread requires locking
          a mutex that is only unlocked by the wait on the simulator thread). Likewise, the simulator thread may{' '}
          <strong>only</strong> purposefully wait when it is expected that the worker thread will signal the completion
          of the work item (if the waiting simulator thread misses the signal it will wait indefinitely).
        </p>
        <p>Note: it is possible to change the synchronization logic for POSIX implementation to make it more flexible.</p>
      </div>
      <p>
        To synchronize shared variables across multiple threads, the simulator can use a mutual exclusion object (i.e. a
        mutex). When requiring exclusive access to a resource, a thread may <em>lock</em> the mutex. Upon finishing
        access to the shared resource, the thread will <em>unlock</em> the mutex to make the resource available for
        other threads. When locking, if the same resource is in use by another thread and the mutex is already locked,
        the lock function will block the thread until the mutex is unlocked. To test if a shared resource is available
        or not, a thread can use <em>try lock</em>, which always returns immediately and indicates whether a lock on the
        mutex has been acquired.
      </p>
      <table className="information" style={{ width: '500px' }}>
        <tbody>
          <tr>
            <th className="strong" style={{ width: '150px' }}>Class</th>
            <th>Description</th>
          </tr>
          <tr>
            <td className="strong">
              <a href="/projects/simworker/code/html/class_c_sim_mutex.html">CSimMutex</a>
            </td>
            <td>Class for mutex objects.</td>
          </tr>
        </tbody>
      </table>
      <NavLinks />

      <h1 id="c5">V. Example</h1>
      <p>
        The following is an example on how simple it is to use worker threads in a program. The following program uses
        10 worker threads to calculate the square of the first 1000 natural numbers and prints each result on the
        screen.
      </p>
      <div className="box-code">
        <pre>{`#include <iostream>
#include "SimWorkers.h"
#include "SimMutex.h"

using namespace std;

class Work
{
private:
    // Here there may be variables that need to be accessed by the work item
    
    CSimMutex    mutex;    // e.g. a mutex to synchronize access to a shared resource,
                           // in this case writing on the screen

public:
    void WorkItemFunction(int input, int* output)
    {
        // First, we calculate the square
        *output = input * input;

        // Next, we print on the screen
        this->mutex.Lock();                             // lock the mutex so other thread cannot interrupt while writing
        cout << input << "^2 = " << *output << endl;    // write on the screen
        this->mutex.Unlock();                           // unlock the mutex to allow other threads access to the shared resource
    }
};

int main(int argc, char** argv)
{
    // Create the worker threads
    CSimWorkers workers(
        10,        // the number of threads
        100        // the thread queue size (we hope for the best, plan for the worst!)
        );

    // Start the worker threads
    workers.Start();

    // Create the object whose function will do the work;
    Work work;

    // Create a list of work items
    CSimWorkItem** workItems = new CSimWorkItem*[1000];

    // Create a variable to store the results
    int output[1000];

    // Create the work items
    for(int index = 0; index < 1000; index++)
    {
        workItems[index] =
            new CSimWorkItemImpl<Work, int, int*>(
                &work,                    // this is the object whose function will do the work
                &Work::WorkItemFunction,  // this is the function that will be executed by the worker thread
                index,                    // this is the first parameter (the input)
                &output[index]            // this is the second parameter (the output)
                );

        // Enqueue the work item to a worker thread
        workers.Enqueue(workItems[index]);
    }

    // We have nothing else to do... let's wait for the work items to complete
    for(int index = 0; index < 1000; index++)
    {
        workItems[index]->Wait();

        // And delete the work item
        delete workItems[index];
    }

    // Delete the list of work items
    delete[] workItems;

    // ... "process" the results

    // Stop the worker threads
    workers.Stop();
}`}</pre>
      </div>
      <p>The output is (numbers are not in order as the work items execute asynchronously):</p>
      <div className="box-code">
        <pre>{`Starting simulator worker threads : 0 1 2 3 4 5 6 7 8 9 done!
6^2 = 36
8^2 = 64
3^2 = 9
...
`}</pre>
      </div>
      <NavLinks />

      <h1 id="c6">VI. Performance Benefit</h1>
      <p>
        The following figure illustrates the execution time of a discrete event simulation for P2P IPTV streaming. This
        simulator code is essentially the same with the exception that certain tasks are executed in parallel using 10
        worker threads on a 16 core system.
      </p>
      <p>
        <img src="/projects/simworker/compare.png" width={560} height={420} alt="Comparison" />
      </p>
      <NavLinks />
      <p className="update">Last updated: March 6, 2011</p>
    </div>
  );
};

export default SimWorkerPage;
